import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { timelineData } from "../data/mock";
import { SectionReveal } from "./SectionReveal";

const TimelineItem = ({ item, index, isVisible, isLast }) => (
  <motion.div
    initial={{ opacity: 0, x: 40, filter: "blur(10px)" }}
    animate={isVisible ? { opacity: 1, x: 0, filter: "blur(0px)" } : {}}
    transition={{ duration: 0.7, delay: index * 0.15, ease: [0.22, 1, 0.36, 1] }}
    className="relative flex gap-6"
  >
    <div className="flex flex-col items-center">
      <motion.div
        initial={{ scale: 0 }}
        animate={isVisible ? { scale: 1 } : {}}
        transition={{ duration: 0.4, delay: index * 0.15 + 0.2, type: "spring", stiffness: 300 }}
        className="w-3.5 h-3.5 rounded-full border-2 border-emerald-500/40 flex-shrink-0 mt-1.5 relative z-10 bg-zinc-50 dark:bg-zinc-950"
      >
        <motion.div
          className="absolute inset-0.5 rounded-full bg-emerald-500 dark:bg-emerald-400"
          initial={{ scale: 0 }}
          animate={isVisible ? { scale: 1 } : {}}
          transition={{ duration: 0.3, delay: index * 0.15 + 0.4 }}
        />
      </motion.div>
      {!isLast && (
        <motion.div
          className="w-px flex-1 min-h-[60px] origin-top"
          style={{ background: "linear-gradient(to bottom, rgba(16,185,129,0.3), rgba(161,161,170,0.1))" }}
          initial={{ scaleY: 0 }}
          animate={isVisible ? { scaleY: 1 } : {}}
          transition={{ duration: 0.6, delay: index * 0.15 + 0.3 }}
        />
      )}
    </div>
    <div className="pb-12">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-xs font-mono text-emerald-600/70 dark:text-emerald-400/70 bg-emerald-500/8 px-2.5 py-1 rounded-md border border-emerald-500/10">
          {item.month ? `${item.month} ${item.year}` : item.year}
        </span>
      </div>
      <h3 className="text-zinc-900 dark:text-zinc-100 font-semibold text-lg">{item.title}</h3>
      <p className="text-zinc-500 text-sm mt-1.5 leading-relaxed max-w-md">{item.description}</p>
    </div>
  </motion.div>
);

const TimelineSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-80px" });

  return (
    <section id="timeline" className="relative py-28 lg:py-36 bg-zinc-50 dark:bg-zinc-950 transition-colors duration-500" ref={sectionRef} aria-label="Career Timeline">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20">
          <div className="lg:sticky lg:top-32 lg:self-start">
            <SectionReveal animation="slideRight">
              <span className="text-sm font-mono text-emerald-600/80 dark:text-emerald-400/80 tracking-widest uppercase">Journey</span>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 dark:text-zinc-50 tracking-tight leading-tight">
                Contribution{" "}<span className="text-emerald-600 dark:text-emerald-400">timeline</span>
              </h2>
              <p className="mt-4 text-zinc-500 text-lg">From joining rtCamp as a trainee to becoming a senior engineer and active contributor across the WordPress ecosystem.</p>
            </SectionReveal>
            <SectionReveal animation="scaleUp" delay={0.3}>
              <div className="mt-10 hidden lg:block">
                <div className="bg-white dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800/50 rounded-xl p-5 shadow-sm dark:shadow-none">
                  <div className="font-mono text-sm space-y-1.5">
                    <p className="text-zinc-400 dark:text-zinc-600">// journey.log</p>
                    <p><span className="text-emerald-600/60 dark:text-emerald-400/60">started:</span>{" "}<span className="text-zinc-600 dark:text-zinc-400">'2021 \u2014 Trainee'</span></p>
                    <p><span className="text-emerald-600/60 dark:text-emerald-400/60">current:</span>{" "}<span className="text-zinc-600 dark:text-zinc-400">'Senior Engineer'</span></p>
                    <p><span className="text-emerald-600/60 dark:text-emerald-400/60">releases:</span>{" "}<span className="text-zinc-700 dark:text-zinc-300">'8 consecutive Core'</span></p>
                    <p><span className="text-emerald-600/60 dark:text-emerald-400/60">status:</span>{" "}<span className="text-zinc-700 dark:text-zinc-300">'actively contributing'</span></p>
                  </div>
                </div>
              </div>
            </SectionReveal>
          </div>
          <div>
            {timelineData.map((item, i) => (
              <TimelineItem key={`${item.year}-${item.title}`} item={item} index={i} isVisible={isInView} isLast={i === timelineData.length - 1} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;
